import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center overflow-hidden">
      {/* Background - masonry stone image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5c6f523828775fad9a2cb0f1516345f3.jpg"
          alt="Valley View Construction - Expert Masonry & Construction in Elkhart Indiana"
          className="w-full h-full object-cover object-center"
        />
        {/* Strong dark overlay for maximum text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/80" />
        {/* Teal tint layer */}
        <div className="absolute inset-0 bg-brand-teal/15" />
      </div>

      {/* Content - fully centered */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center min-h-screen px-4 md:px-8 pt-24 pb-16">

        {/* BIG CENTERED LOGO */}
        <div
          className={`flex justify-center mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'
          }`}
        >
          <img
            src="https://storage.readdy-site.link/project_files/46b5028d-8e1d-4b10-b368-77259c0a6540/f47a4515-632d-48b9-9db8-560f30e0850b_VALLEY-VIEW-CONSTRUCTION-LLC_logo_white.png?v=d3d0dec20be7cdc5ea07dc6e0ae5efd5"
            alt="Valley View Construction Logo"
            className="h-36 md:h-48 lg:h-56 w-auto object-contain drop-shadow-2xl"
          />
        </div>

        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 bg-brand-teal/20 border border-brand-teal/40 rounded-full mb-6 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-2 h-2 bg-brand-teal-light rounded-full animate-pulse flex-shrink-0" />
          <span className="text-white text-xs font-bold uppercase tracking-widest whitespace-nowrap">
            Elkhart, Indiana · Serving All Michiana
          </span>
        </div>

        {/* Main Headline - centered */}
        <h1
          className={`font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 text-center transition-all duration-700 delay-150 drop-shadow-lg ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="block drop-shadow-md">Crafting Michiana's</span>
          <span className="block text-brand-teal-light italic drop-shadow-md">Finest Stonework</span>
          <span className="block text-white drop-shadow-md">&amp; Construction</span>
        </h1>

        {/* Subheadline - centered */}
        <p
          className={`text-lg md:text-xl text-white font-light leading-relaxed mb-8 max-w-2xl text-center transition-all duration-700 delay-200 drop-shadow-sm ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.7)' }}
        >
          From expert masonry and stone work to hardscape patios, outdoor kitchens, chimneys, and full construction — we build legacies that last generations.
        </p>

        {/* CTA Buttons - centered */}
        <div
          className={`flex flex-col sm:flex-row gap-4 mb-12 justify-center transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            onClick={() => scrollToSection('contact')}
            className="px-8 py-4 bg-brand-teal hover:bg-brand-teal-light text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-base flex items-center justify-center gap-2"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-mail-send-line" />
            </div>
            Get a Free Quote
          </button>
          <Link
            to="/services"
            className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-base flex items-center justify-center gap-2 backdrop-blur-sm"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-tools-line" />
            </div>
            Explore Services
          </Link>
        </div>

        {/* Stats Row - centered */}
        <div
          className={`flex flex-wrap gap-8 md:gap-14 justify-center transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {[
            { value: '500+', label: 'Projects Completed' },
            { value: '30mi', label: 'Service Radius' },
            { value: '100%', label: 'Licensed & Insured' },
            { value: '24/7', label: 'Emergency Service' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold font-serif text-brand-teal-light drop-shadow-md">{stat.value}</div>
              <div className="text-xs text-white font-semibold uppercase tracking-wide mt-0.5" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/50 rounded-full" />
        </div>
      </div>

      {/* Phone Badge */}
      <a
        href="tel:+15744096118"
        className="absolute bottom-8 right-4 md:right-8 flex items-center gap-3 bg-brand-dark/80 backdrop-blur-sm border border-brand-teal/20 rounded-xl px-4 py-3 hover:border-brand-teal/50 transition-all"
      >
        <div className="w-9 h-9 flex items-center justify-center bg-brand-teal rounded-lg">
          <i className="ri-phone-fill text-white text-lg" />
        </div>
        <div>
          <p className="text-xs text-white/50 font-medium">Call Us Free</p>
          <p className="text-sm font-bold text-white">574-409-6118</p>
        </div>
      </a>
    </section>
  );
};

export default HeroSection;
